import {
  faCircleNotch,
  faTriangleExclamation,
  faUserSlash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type FC, useEffect, useState } from "react";
import { useApi, useFetchedResource } from "../api/APIContext";
import { useApiCall } from "../api/api";
import type { CheckInVisitor, CheckInVisitorStatus } from "../api/dto/CheckInVisitor";
import type { ProjectCheckIn } from "../api/dto/ProjectCheckIn";
import { Badge, BadgeStyle } from "../components/data/Badge";
import { TablePlaceholder } from "../components/data/TablePlaceholder";
import { formatDateTime, formatRemaining } from "../util/formatTime";

const Detail: FC<{ label: string; value: string }> = (props) => (
  <div>
    <div className={"text-[10px] font-semibold uppercase tracking-wider text-slate-400"}>
      {props.label}
    </div>
    <div className={"text-xs text-slate-700"}>{props.value}</div>
  </div>
);

/** The icon-only "Toegang intrekken" button, on the right of a visitor's details. */
const RevokeIconButton: FC<{ onClick: () => void }> = (props) => (
  <button
    type={"button"}
    title={"Toegang intrekken"}
    aria-label={"Toegang intrekken"}
    onClick={props.onClick}
    className={
      "flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-red-200 text-red-500 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
    }
  >
    <FontAwesomeIcon icon={faUserSlash} className={"h-3.5 w-3.5"} />
  </button>
);

/**
 * Asks to confirm revoking a visitor's access, so a visitor isn't cut off by a stray click: a short explanation with
 * a confirm and a cancel button.
 */
const RevokeConfirmation: FC<{
  name: string | null;
  onCancel: () => void;
  onRevoke: () => Promise<void>;
}> = (props) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const revoke = async () => {
    setBusy(true);
    setError(false);
    try {
      await props.onRevoke();
    } catch {
      setError(true);
      setBusy(false);
    }
    // On success the visitor is shown as revoked, which replaces this whole panel.
  };

  return (
    <fieldset
      disabled={busy}
      className={"mt-3 min-w-0 rounded-lg border border-red-200 bg-red-50 p-3"}
    >
      <legend className={"sr-only"}>Toegang intrekken bevestigen</legend>
      <div className={"flex items-start gap-2.5"}>
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className={"mt-0.5 h-3.5 w-3.5 text-red-500"}
        />
        <div className={"min-w-0 text-xs"}>
          <div className={"font-semibold text-red-900"}>
            Toegang van {props.name ?? "deze bezoeker"} intrekken?
          </div>
          <div className={"mt-0.5 text-red-800/80"}>
            De toegang tot het project wordt direct beëindigd. Om weer toegang te krijgen moet de bezoeker opnieuw inchecken met een nieuwe toegangscode.
          </div>
        </div>
      </div>
      {error && (
        <div className={"mt-2 text-xs font-medium text-red-700"}>
          Intrekken is mislukt. Probeer het opnieuw.
        </div>
      )}
      <div className={"mt-3 flex justify-end gap-2"}>
        <button
          type={"button"}
          disabled={busy}
          onClick={props.onCancel}
          className={
            "rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          }
        >
          Annuleren
        </button>
        <button
          type={"button"}
          disabled={busy}
          onClick={revoke}
          className={
            "inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-70"
          }
        >
          {busy && <FontAwesomeIcon icon={faCircleNotch} spin={true} className={"h-3 w-3"} />}
          {busy ? "Bezig met intrekken..." : "Ja, toegang intrekken"}
        </button>
      </div>
    </fieldset>
  );
};

const VisitorRow: FC<{
  visitor: CheckInVisitor;
  name: string | null;
  email: string | null;
  initials: string;
  onRevoke: () => Promise<void>;
}> = (props) => {
  const { visitor } = props;
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const status = visitor.status;
  const inactive = status !== "active";
  return (
    <li className={`flex items-start gap-3 py-3 ${inactive ? "opacity-70" : ""}`}>
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
          inactive ? "bg-slate-100 text-slate-400" : "bg-brand-100 text-brand-700"
        }`}
      >
        {props.initials}
      </div>
      <div className={"min-w-0 flex-1"}>
        <div className={"flex items-start justify-between gap-2"}>
          <div className={"min-w-0"}>
            <div
              className={`truncate text-sm font-medium ${inactive ? "text-slate-600" : "text-brand-900"}`}
            >
              {props.name ?? <span className={"font-mono text-xs"}>{visitor.userUuid}</span>}
            </div>
            {props.email && <div className={"truncate text-xs text-slate-500"}>{props.email}</div>}
          </div>
          {status === "revoked" && <Badge style={BadgeStyle.dark}>Ingetrokken</Badge>}
          {status === "expired" && <Badge style={BadgeStyle.danger}>Verlopen</Badge>}
          {status === "active" && <Badge style={BadgeStyle.success}>Actief</Badge>}
        </div>
        <div className={"mt-1.5 flex items-end justify-between gap-3"}>
          <div className={"grid min-w-0 flex-1 grid-cols-2 gap-3"}>
            <Detail label={"Ingecheckt"} value={formatDateTime(visitor.checkedInAt)} />
            {status === "revoked" ? (
              <Detail label={"Ingetrokken op"} value={formatDateTime(visitor.revokedAt)} />
            ) : (
              <Detail
                label={status === "expired" ? "Verlopen op" : "Toegang tot"}
                value={
                  visitor.expiresAt === null
                    ? "-"
                    : status === "expired"
                      ? formatDateTime(visitor.expiresAt)
                      : `${formatDateTime(visitor.expiresAt)} (${formatRemaining(visitor.expiresAt)})`
                }
              />
            )}
          </div>
          {status !== "revoked" && !confirmingRevoke && (
            <RevokeIconButton onClick={() => setConfirmingRevoke(true)} />
          )}
        </div>
        {status !== "revoked" && confirmingRevoke && (
          <RevokeConfirmation
            name={props.name}
            onCancel={() => setConfirmingRevoke(false)}
            onRevoke={props.onRevoke}
          />
        )}
      </div>
    </li>
  );
};

export const CheckInVisitorsModal: FC<{ checkIn: ProjectCheckIn }> = (props) => {
  const { users } = useApi();
  const { listCheckInVisitors, revokeProjectAccess } = useApiCall();
  const visitors = useFetchedResource((checkInId: string) => listCheckInVisitors(checkInId));

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per check-in
  useEffect(() => {
    visitors.reload(props.checkIn.uuid);
  }, [props.checkIn.uuid]);

  if (visitors.error) {
    return <TablePlaceholder>Bezoekers laden is mislukt</TablePlaceholder>;
  }
  if (visitors.loading || visitors.resource === null) {
    return <TablePlaceholder>Bezoekers laden...</TablePlaceholder>;
  }
  if (visitors.resource.length === 0) {
    return <TablePlaceholder>Nog niemand ingecheckt</TablePlaceholder>;
  }
  const statusOrder = { active: 0, expired: 1, revoked: 2 };
  const sortedVisitors = [...visitors.resource].sort(
    (a, b) => statusOrder[a.status] - statusOrder[b.status],
  );
  const countOf = (status: CheckInVisitorStatus) =>
    sortedVisitors.filter((visitor) => visitor.status === status).length;
  return (
    <div className={"pb-3"}>
      <div className={"flex items-center justify-between border-b border-slate-100 pb-2 text-xs"}>
        <span className={"font-medium text-slate-600"}>
          {sortedVisitors.length} {sortedVisitors.length === 1 ? "bezoeker" : "bezoekers"}
        </span>
        <span className={"text-slate-500"}>
          {countOf("active")} actief &middot; {countOf("expired")} verlopen &middot;{" "}
          {countOf("revoked")} ingetrokken
        </span>
      </div>
      <ul className={"max-h-[28rem] divide-y divide-slate-100 overflow-y-auto"}>
        {sortedVisitors.map((visitor) => {
          const user = users.find((u) => u.id === visitor.userUuid);
          return (
            <VisitorRow
              key={visitor.userUuid}
              visitor={visitor}
              name={user ? `${user.first_name} ${user.last_name}` : null}
              email={user?.email ?? null}
              onRevoke={async () => {
                await revokeProjectAccess(visitor.userUuid, props.checkIn.projectUuid);
                await visitors.reload(props.checkIn.uuid, true);
              }}
              initials={
                user
                  ? `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() || "?"
                  : "?"
              }
            />
          );
        })}
      </ul>
    </div>
  );
};
